// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { AuthGuard, GetAuthRoutes, Loading, useAuthContext } from '@tls/auth';
import { useAuthenticatedHub } from '@tls/auth/signalr';
import Evaluate from 'components/project/Evaluate';
import Label from 'components/project/Label';
import List from 'components/projectSelection/List';
import Tiles from 'components/projectSelection/Tiles';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppRoutes } from 'utilities/router/AppRoutes';
import Nav from './Nav';
import AppController from './AppController';
import NotFound from 'pages/NotFound';
import Project from 'pages/Project';
import Projects from 'pages/Projects';
import NoAccess from 'pages/NoAccess';
import Settings from 'pages/Settings';
import Users from 'components/settings/Users';
import AddUser from 'components/settings/AddUser';
import Contracts from 'components/settings/Contracts/Contracts';
import { hub } from 'hub/Connector';
import Roles from 'components/settings/Roles';
import AddRole from 'components/settings/AddRole';
import Versions from 'components/settings/Versions';
import NotificationSettings from 'components/settings/NotificationSettings';
import Data from 'components/project/Data';
import { AlgorithmProgrammingContextProvider } from '@tls/sw91_ui_templates';
import Algorithms from 'components/project/Algorithms';

export default function ApplicationRouter() {
  const { hasAccess, appBaseUrl, authService, logo, progressBar } = useAuthContext();

  const signalRConnectionEstablished = useAuthenticatedHub(hub);

  if (!hasAccess && authService?.isAuthenticated()) return <NoAccess />;

  // Ensure signalR connection is established, otherwise we can't receive pictures on page refresh
  if (!signalRConnectionEstablished) {
    return (
      <Routes>
        <Route element={<Nav />}>
          {GetAuthRoutes()}
          <Route
            element={
              <AuthGuard>
                <AppController />
              </AuthGuard>
            }
          >
            <Route
              path='*'
              element={
                <Loading basePath={appBaseUrl} logo={logo}>
                  {progressBar}
                </Loading>
              }
            />
          </Route>
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Nav />}>
        {GetAuthRoutes()}
        <Route path='/' element={<Navigate to={AppRoutes.Projects} />} />
        <Route
          element={
            <AuthGuard>
              <AppController />
            </AuthGuard>
          }
        >
          <Route path={AppRoutes.Projects} element={<Projects />}>
            <Route path={AppRoutes.ProjectsTilesSubPath} element={<Tiles />} />
            <Route path={AppRoutes.ProjectsListSubPath} element={<List />} />
          </Route>
          <Route path={`${AppRoutes.Project}/:id`} element={<Project />}>
            <Route path={AppRoutes.ProjectLabelSubpath} element={<Label />} />
            <Route
              path={AppRoutes.ProjectAlgorithmsSubpath}
              element={
                <AlgorithmProgrammingContextProvider persistToLocalStorage={false}>
                  <Algorithms />
                </AlgorithmProgrammingContextProvider>
              }
            />
            <Route path={AppRoutes.ProjectDataSubpath} element={<Data />} />
            <Route path={AppRoutes.ProjectEvaluationSubPath} element={<Evaluate />} />
          </Route>
          <Route path={AppRoutes.Settings} element={<Settings />}>
            <Route path={AppRoutes.SettingsUserSubpath} element={<Users />} />
            <Route path={AppRoutes.SettingsAddUser} element={<AddUser />} />
            <Route path={AppRoutes.SettingsRoleSubpath} element={<Roles />} />
            <Route path={AppRoutes.SettingsAddRole} element={<AddRole />} />
            <Route path={AppRoutes.SettingsContracts} element={<Contracts />} />
            <Route path={AppRoutes.Versions} element={<Versions />} />
            <Route path={AppRoutes.SettingsNotifications} element={<NotificationSettings />} />
          </Route>
        </Route>
        <Route path='*' element={<NotFound />} />
      </Route>
    </Routes>
  );
}
